<template>
  <ul class="nav nav-pills flex-column list-unstyled mb-0 flex-shrink-0">
    <SidenavElement
      v-if="showConsolidatedEntry && hasMultipleCompanies"
      :itemClass="getCompanyItemClass(undefined, { isConsolidated: true })"
      :to="{ path: '/dashboard', query: { consolidated: 'true' } }"
      :isActive="isDashboardRoute && consolidated"
      icon="fa-solid fa-chart-line"
      :text="$t('dashboard.consolidated_view')"
      class="fw-bold"
    />

    <!-- Portal-specific sections that belong with the company list (Soldera: Compliance Groups) -->
    <slot name="sections" />

    <!-- Search box for 6+ companies -->
    <template v-if="isDenseCompanyList">
      <SidenavElement
        itemClass="nav-item mb-2"
        :isActive="false"
        :disableHover="true"
      >
        <template #default>
          <div class="company-search-container">
            <div
              class="d-flex align-items-center justify-content-between text-uppercase mb-2 company-search-header"
            >
              <span>{{ $t("dashboard.companies") }}</span>
              <span class="badge bg-gray-700 text-white">
                {{ filteredCompanies.length }}/{{ companyCount }}
              </span>
            </div>
            <div class="input-group input-group-sm">
              <span class="input-group-text">
                <font-awesome-icon icon="fa-solid fa-magnifying-glass" />
              </span>
              <input
                v-model="companySearchTerm"
                type="search"
                class="form-control"
                :placeholder="$t('common.search_companies')"
                :aria-label="$t('common.search_companies')"
                autocomplete="off"
              />
              <button
                v-if="hasSearch"
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click="resetCompanySearch"
              >
                <font-awesome-icon icon="fa-solid fa-xmark" />
              </button>
            </div>
            <button
              v-if="actionableCompaniesCount > 0"
              type="button"
              class="sidebar-action-items-btn w-100 d-flex align-items-center justify-content-between mt-3"
              @click="focusFirstActionableCompany"
            >
              <span class="d-flex align-items-center gap-2">
                <span class="sidebar-group-action-dot" />
                <span class="fw-semibold">
                  {{ $t("common.action_items") }}
                </span>
              </span>
              <span class="badge bg-primary text-white fw-semibold">
                {{ actionableCompaniesCount }}
              </span>
            </button>
          </div>
        </template>
      </SidenavElement>
    </template>
  </ul>

  <div class="sidebar-company-list-wrapper">
    <ul
      ref="companyListRef"
      class="sidebar-company-list nav nav-pills flex-column flex-nowrap flex-grow-1 list-unstyled mb-0"
      :class="{ 'is-scrolling': isCompanyListScrolling }"
    >
      <!-- For 6+ companies -->
      <template v-if="isDenseCompanyList">
        <SidenavElement
          v-if="filteredCompanies.length === 0"
          itemClass="nav-item mb-2"
          :isActive="false"
          :disableHover="true"
        >
          <template #default>
            <div class="text-muted small d-flex align-items-center">
              <font-awesome-icon icon="fa-solid fa-circle-info" class="me-2" />
              {{ $t("common.no_results") }}
            </div>
          </template>
        </SidenavElement>

        <template v-else-if="shouldGroupCompaniesByCountry">
          <template v-for="group in groupedFilteredCompanies" :key="group.code">
            <li class="sidebar-country-group mb-2">
              <button
                type="button"
                class="sidebar-country-toggle"
                :class="{
                  'is-searching': hasSearch,
                  'is-locked': isCountryGroupLocked(group.code),
                }"
                :aria-expanded="isCountryGroupExpanded(group.code)"
                @click="toggleCountryGroup(group.code)"
              >
                <span class="sidebar-country-toggle__label">
                  {{ group.label }}
                </span>
                <span class="d-flex align-items-center gap-2">
                  <span
                    v-if="group.hasActionableCompany"
                    class="sidebar-group-action-dot"
                  />
                  <span class="badge bg-gray-700 text-white">
                    {{ group.companies.length }}
                  </span>
                  <font-awesome-icon
                    icon="fa-solid fa-chevron-down"
                    class="sidebar-country-toggle__chevron"
                    :class="{
                      'is-expanded': isCountryGroupExpanded(group.code),
                    }"
                  />
                </span>
              </button>

              <div
                class="sidebar-country-collapse"
                :class="{
                  'is-expanded': isCountryGroupExpanded(group.code),
                }"
              >
                <ul class="sidebar-country-list">
                  <CompanySidenavEntry
                    v-for="company in group.companies"
                    :key="`${group.code}-${company.id ?? company.name}`"
                    :company
                    :baseItemClass="getCompanyItemClass(company.id)"
                    :isActive="isSelectedCompany(company.id)"
                    :hasAction="companyHasAction(company.id)"
                    :followupState="getFollowupState(company.id)"
                    :adminId="accountData.admin_account_id ? company.id : null"
                    nested
                  />
                </ul>
              </div>
            </li>
          </template>
        </template>

        <template v-else>
          <CompanySidenavEntry
            v-for="company in filteredCompanies"
            :key="company.id ?? company.name"
            :company
            :baseItemClass="getCompanyItemClass(company.id)"
            :isActive="isSelectedCompany(company.id)"
            :hasAction="companyHasAction(company.id)"
            :followupState="getFollowupState(company.id)"
            :adminId="accountData.admin_account_id ? company.id : null"
          />
        </template>
      </template>

      <!-- For 1-5 companies -->
      <template v-else>
        <CompanySidenavEntry
          v-for="company in accountData.companies"
          :key="company.id ?? company.name"
          :company
          :baseItemClass="getCompanyItemClass(company.id)"
          :isActive="isSelectedCompany(company.id)"
          :hasAction="companyHasAction(company.id)"
          :followupState="getFollowupState(company.id)"
          :adminId="accountData.admin_account_id ? company.id : null"
          :useDefaultSlot="false"
        />
      </template>
    </ul>
    <ScrollMoreIndicator :remainingCount="hiddenCompanyCount" />
  </div>
</template>

<script setup lang="ts">
import ScrollMoreIndicator from "@/components/common/ScrollMoreIndicator.vue";
import { useCompanyFinder } from "@/composables/useCompanyFinder";
import { useConsolidated } from "@/composables/useConsolidated";
import {
  computeFollowupState,
  type FollowupState,
} from "@/composables/useFollowupState";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { Account, Company } from "@/types";
import {
  groupCompaniesByCountry,
  normalizeCountryCode,
  translateCountryCode,
  type CountryGroup,
} from "@/utils/countries";
import { useEventListener, useTimeoutFn } from "@vueuse/core";
import { computed, onUnmounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import CompanySidenavEntry from "./CompanySidenavEntry.vue";
import SidenavElement from "./SidenavElement.vue";

// The company-switching list shared by every portal sidenav: search + country
// grouping for dense lists, scroll indicator, and (optionally) the
// consolidated-view entry and producer follow-up nudges.

// Below this threshold, the sidebar renders a flat list.
// At or above it, a search box is shown and (when 2+ countries exist) companies
// are grouped by country.
const MIN_COMPANIES_FOR_GROUPING = 6;
// Below this threshold, only a single company is shown (no consolidated view).
const MIN_COMPANIES_FOR_CONSOLIDATED = 2;
// A country group is only meaningful if there's at least one other country to
// contrast it against.
const MIN_COUNTRIES_FOR_GROUPING = 2;

interface CompanyCountryGroup extends CountryGroup<Company> {
  hasActionableCompany: boolean;
}

const props = withDefaults(
  defineProps<{
    accountData: Account;
    /** Render the consolidated-view entry (Soldera producer portal only). */
    showConsolidatedEntry?: boolean;
    /** Compute follow-up nudges and fetch pending invoice items. */
    showFollowups?: boolean;
  }>(),
  { showConsolidatedEntry: true, showFollowups: true }
);

const { globalErrorHandler } = useGlobalHandlers();

const companyListRef = useTemplateRef<HTMLElement>("companyListRef");
const isCompanyListScrolling = ref(false);
const hiddenCompanyCount = ref(0);
const expandedCountryGroups = ref<string[]>([]);

const updateHiddenCount = () => {
  const list = companyListRef.value;
  if (!list) {
    hiddenCompanyCount.value = 0;
    return;
  }
  const listRect = list.getBoundingClientRect();
  let hidden = 0;
  for (const child of list.querySelectorAll<HTMLElement>(".company-entry")) {
    if (child.closest(".sidebar-country-collapse:not(.is-expanded)")) {
      continue;
    }
    const childRect = child.getBoundingClientRect();
    if (childRect.top >= listRect.bottom) {
      hidden++;
    }
  }
  hiddenCompanyCount.value = hidden;
};

const { start: restartScrollFade } = useTimeoutFn(
  () => {
    isCompanyListScrolling.value = false;
  },
  1000,
  { immediate: false }
);

useEventListener(companyListRef, "scroll", () => {
  updateHiddenCount();
  isCompanyListScrolling.value = true;
  restartScrollFade();
});

useEventListener(companyListRef, "transitionend", updateHiddenCount);

const companyListObserver = ref<MutationObserver | null>(null);
const companyListResizeObserver = ref<ResizeObserver | null>(null);

watch(companyListRef, (el, oldEl) => {
  if (oldEl) {
    companyListObserver.value?.disconnect();
    companyListResizeObserver.value?.disconnect();
  }
  if (el) {
    updateHiddenCount();
    if (!companyListObserver.value) {
      companyListObserver.value = new MutationObserver(updateHiddenCount);
    }
    companyListObserver.value.observe(el, { childList: true, subtree: true });

    if (!companyListResizeObserver.value) {
      companyListResizeObserver.value = new ResizeObserver(updateHiddenCount);
    }
    companyListResizeObserver.value.observe(el);
  } else {
    hiddenCompanyCount.value = 0;
  }
});

onUnmounted(() => {
  companyListObserver.value?.disconnect();
  companyListObserver.value = null;
  companyListResizeObserver.value?.disconnect();
  companyListResizeObserver.value = null;
});

const { consolidated } = useConsolidated();

const { selectedCompanyOrFirst } = useCompanyFinder(props.accountData);

const router = useRouter();
const route = useRoute();
const { t, te, locale } = useI18n();

const companySearchTerm = ref("");
const companiesPendingInvoiceItems = ref<Record<number, boolean>>({});

const isDashboardRoute = computed(() => {
  return route.name === "Dashboard";
});

const companyFollowupStates = computed<Record<number, FollowupState>>(() => {
  const states: Record<number, FollowupState> = {};
  if (!props.showFollowups) {
    return states;
  }
  for (const company of props.accountData.companies) {
    if (!company.id) {
      continue;
    }
    const companyId = company.id;
    const hasPendingInvoiceItems =
      companiesPendingInvoiceItems.value[companyId] ?? false;
    states[companyId] = computeFollowupState(
      company,
      props.accountData.id,
      hasPendingInvoiceItems,
      t
    );
  }
  return states;
});

const companyCount = computed(() => props.accountData.companies.length);

const actionableCompanyIds = computed(() => {
  const actionable = new Set<number>();
  for (const company of props.accountData.companies) {
    if (!company.id) {
      continue;
    }
    const state = companyFollowupStates.value[company.id];
    if (state?.displayType === "button") {
      actionable.add(company.id);
    }
  }
  return actionable;
});

const actionableCompaniesCount = computed(
  () => actionableCompanyIds.value.size
);

const sortedCompanies = computed(() =>
  [...props.accountData.companies].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "")
  )
);

const getCountryLabel = (countryCode: string) =>
  translateCountryCode(countryCode, t, te);

const filteredCompanies = computed(() => {
  const searchTerm = companySearchTerm.value.trim().toLowerCase();
  const list = sortedCompanies.value;

  if (!searchTerm) {
    return list;
  }

  return list.filter((company) =>
    (company.name || "").toLowerCase().includes(searchTerm)
  );
});

const hasSearch = computed(() => companySearchTerm.value.trim().length > 0);

const resetCompanySearch = () => {
  companySearchTerm.value = "";
};

const companyHasAction = (companyId?: number): boolean => {
  if (typeof companyId !== "number") {
    return false;
  }

  return actionableCompanyIds.value.has(companyId);
};

const isSelectedCompany = (companyId?: number): boolean =>
  isDashboardRoute.value &&
  !consolidated.value &&
  selectedCompanyOrFirst.value?.id === companyId;

const isDenseCompanyList = computed(
  () => companyCount.value >= MIN_COMPANIES_FOR_GROUPING
);

const hasMultipleCompanies = computed(
  () => companyCount.value >= MIN_COMPANIES_FOR_CONSOLIDATED
);

const shouldGroupCompaniesByCountry = computed(() => {
  const countries = new Set(
    sortedCompanies.value.map((company) =>
      normalizeCountryCode(company.country)
    )
  );

  return (
    countries.size >= MIN_COUNTRIES_FOR_GROUPING &&
    sortedCompanies.value.length >= MIN_COMPANIES_FOR_GROUPING
  );
});

// Shares the canonical country bucketing/sort with the consolidated selector
// (groupCompaniesByCountry), layering on the sidebar-specific actionable flag.
const groupedFilteredCompanies = computed<CompanyCountryGroup[]>(() =>
  groupCompaniesByCountry(
    filteredCompanies.value,
    getCountryLabel,
    locale.value
  ).map((group) => ({
    ...group,
    hasActionableCompany: group.companies.some((company) =>
      actionableCompanyIds.value.has(company.id ?? -1)
    ),
  }))
);

const getFollowupState = (companyId?: number): FollowupState => {
  if (typeof companyId !== "number") {
    return { displayType: "none", actionType: "none" };
  }

  return (
    companyFollowupStates.value[companyId] ?? {
      displayType: "none",
      actionType: "none",
    }
  );
};

const focusFirstActionableCompany = () => {
  const [firstActionId] = actionableCompanyIds.value;
  if (firstActionId === undefined) {
    return;
  }

  router.push({
    path: "/dashboard",
    query: { company_id: firstActionId.toString(), consolidated: "false" },
  });
};

const activeCompanyCountryCode = computed(() => {
  if (consolidated.value) {
    return undefined;
  }

  return selectedCompanyOrFirst.value?.country
    ? normalizeCountryCode(selectedCompanyOrFirst.value.country)
    : undefined;
});

watch(
  [
    shouldGroupCompaniesByCountry,
    groupedFilteredCompanies,
    hasSearch,
    activeCompanyCountryCode,
  ],
  ([shouldGroup, countryGroups, isSearching, activeCountryCode]) => {
    if (!shouldGroup) {
      expandedCountryGroups.value = [];
      return;
    }

    const availableCountryCodes = countryGroups.map((group) => group.code);
    const preservedCountryCodes = isSearching
      ? availableCountryCodes
      : expandedCountryGroups.value.filter((countryCode) =>
          availableCountryCodes.includes(countryCode)
        );

    const defaultCountryCode =
      activeCountryCode && availableCountryCodes.includes(activeCountryCode)
        ? activeCountryCode
        : (preservedCountryCodes[0] ?? availableCountryCodes[0]);

    expandedCountryGroups.value = isSearching
      ? preservedCountryCodes
      : defaultCountryCode
        ? Array.from(new Set([defaultCountryCode, ...preservedCountryCodes]))
        : preservedCountryCodes;
  },
  { immediate: true }
);

const isCountryGroupExpanded = (countryCode: string) => {
  return expandedCountryGroups.value.includes(countryCode);
};

const isCountryGroupLocked = (countryCode: string) => {
  return (
    isCountryGroupExpanded(countryCode) &&
    activeCompanyCountryCode.value === countryCode
  );
};

const toggleCountryGroup = (countryCode: string) => {
  if (hasSearch.value) {
    return;
  }

  if (isCountryGroupExpanded(countryCode)) {
    if (activeCompanyCountryCode.value === countryCode) {
      return;
    }

    expandedCountryGroups.value = expandedCountryGroups.value.filter(
      (expandedCountryCode) => expandedCountryCode !== countryCode
    );
    return;
  }

  expandedCountryGroups.value = [...expandedCountryGroups.value, countryCode];
};

const getCompanyItemClass = (
  companyId?: number,
  options?: { isConsolidated?: boolean }
) => {
  const classes: Array<string | Record<string, boolean>> = [
    "nav-item",
    "mb-2",
    "company-entry",
    "w-100",
  ];

  if (options?.isConsolidated) {
    classes.push("company-entry--consolidated");
  }

  const isActiveCompany = options?.isConsolidated
    ? isDashboardRoute.value && consolidated.value
    : companyId !== undefined && !consolidated.value
      ? isDashboardRoute.value && selectedCompanyOrFirst.value?.id === companyId
      : false;

  const hasAction = companyHasAction(companyId);

  classes.push({
    "company-entry--active": isActiveCompany,
    "company-entry--action": hasAction,
  });

  return classes;
};

// Pending-invoice nudges are producer-workflow data; fetch once per mounted
// list, and only for portals that show follow-ups. The flag doubles as an
// in-flight guard and is reset on failure so a later companies change can
// retry.
let pendingInvoicesFetched = false;
watch(
  () => props.accountData.companies.length,
  async (companyCountValue) => {
    if (!props.showFollowups || pendingInvoicesFetched || !companyCountValue) {
      return;
    }
    pendingInvoicesFetched = true;
    try {
      const response = await goSolidApi.get(
        "/api/companies/pending-invoice-items"
      );
      const hasPendingItems = response.data.has_pending_items ?? {};

      Object.entries(hasPendingItems).forEach(([companyId, hasPending]) => {
        companiesPendingInvoiceItems.value[Number(companyId)] =
          hasPending as boolean;
      });
    } catch (error: unknown) {
      pendingInvoicesFetched = false;
      globalErrorHandler(error);
    }
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.sidebar-company-list-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
  flex-basis: 10rem;
  width: calc(100% + 0.5rem);
  margin-right: -0.5rem;
  padding-right: 0.5rem;
  overflow-x: clip;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);

  :deep(.scroll-indicator-badge) {
    background: rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.8);
  }

  :deep(.scroll-bottom-shadow) {
    background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.35));
  }
}

.sidebar-company-list {
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 0;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &.is-scrolling {
    scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
  }
}

.sidebar-country-group {
  list-style: none;
  width: 100%;
  min-width: 0;
}

.sidebar-country-toggle {
  width: 100%;
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.25rem 1rem 0.25rem 0.75rem;
  text-align: left;
}

.sidebar-country-toggle.is-searching {
  cursor: default;
}

.sidebar-country-toggle.is-locked {
  cursor: default;
}

.sidebar-country-toggle.is-locked .sidebar-country-toggle__chevron {
  opacity: 0.3;
}

.sidebar-country-toggle__label {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
}

.sidebar-country-toggle__chevron {
  transform: rotate(-90deg);
  transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.sidebar-country-toggle__chevron.is-expanded {
  transform: rotate(0deg);
}

.sidebar-country-collapse {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.sidebar-country-collapse.is-expanded {
  grid-template-rows: 1fr;
}

.sidebar-country-list {
  list-style: none;
  width: 100%;
  min-width: 0;
  min-height: 0;
  margin: 0;
  padding: 0;
  overflow: hidden;
}

.sidebar-country-collapse.is-expanded .sidebar-country-list {
  padding-top: 0.375rem;
}

.company-entry {
  width: 100%;
  min-width: 0;
}

.company-entry .nav-link {
  width: 100%;
}

.company-entry--active {
  position: relative;
  z-index: 1;
}

.company-entry--active::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  right: -1rem;
  width: 1rem;
  background-color: var(--phoenix-body-highlight-bg);
  pointer-events: none;
}

.company-entry__content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.company-entry--nested .nav-link {
  padding-left: 1rem;
}

.company-entry--consolidated .nav-link {
  border-left: 3px solid rgba(var(--phoenix-primary-rgb), 0.8);
}

.sidebar-group-action-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--phoenix-orange-50);
  flex-shrink: 0;
}

.sidebar-action-items-btn {
  padding: 0.5rem 0.75rem;
  font-size: 0.8125rem;
  border-radius: 0.375rem;
  border: 1px solid rgba(var(--phoenix-orange-50-rgb, 245, 210, 110), 0.45);
  background: rgba(var(--phoenix-orange-50-rgb, 245, 210, 110), 0.12);
  color: var(--phoenix-orange-50);
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: rgba(var(--phoenix-orange-50-rgb, 245, 210, 110), 0.22);
    border-color: rgba(var(--phoenix-orange-50-rgb, 245, 210, 110), 0.6);
  }
}

.company-search-container {
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);

  .company-search-header {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: rgba(255, 255, 255, 0.72);
    margin-bottom: 0.625rem;
  }

  .input-group .form-control,
  .input-group .input-group-text,
  .input-group .btn-outline-secondary {
    background-color: rgba(255, 255, 255, 0.06) !important;
    border-color: rgba(255, 255, 255, 0.12) !important;
    color: rgba(255, 255, 255, 0.4);
  }

  .input-group .form-control {
    color: rgba(255, 255, 255, 0.85);

    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }
  }

  .input-group .form-control:focus {
    background-color: rgba(255, 255, 255, 0.1) !important;
    border-color: var(--phoenix-primary) !important;
    box-shadow: 0 0 0 0.2rem rgba(var(--phoenix-primary-rgb), 0.15);
    color: rgba(255, 255, 255, 0.9);
  }

  .input-group .btn-outline-secondary:hover {
    background-color: rgba(255, 255, 255, 0.12) !important;
    border-color: rgba(255, 255, 255, 0.2) !important;
    color: var(--phoenix-body-color);
  }
}
</style>
